/** Validation limits and protocol constants shared by both sides of the wire. */

export const LIMITS = {
  email: { max: 254 },
  name: { min: 2, max: 100 },
  password: { min: 10, max: 128 },
  phone: { minDigits: 7, maxDigits: 15 },
  city: { max: 100 },
  message: { min: 10, max: 4000 },
  /** The requirement of an enquiry (D-327): thickness in mm, a quantity (sq ft or slabs) and a free-text size. */
  enquiryThicknessMm: { min: 1, max: 500 },
  enquiryQuantity: { min: 1, max: 1_000_000 },
  enquirySize: { max: 80 },
  reason: { max: 500 },
  shortDescription: { max: 280 },
  slug: { max: 120 },
  search: { max: 100 },
  page: { default: 24, max: 60 },
  upload: {
    imageMaxBytes: 20 * 1024 * 1024,
    attachmentMaxBytes: 8 * 1024 * 1024,
    imageMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/avif'],
    attachmentMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
    maxImagePixels: 50_000_000,
  },
  otp: { length: 6, ttlMinutes: 10, maxAttempts: 5 },
  tokens: { verifyEmailHours: 48, resetPasswordMinutes: 30, changeEmailHours: 24 },
  resendCooldownSeconds: 60,
} as const;

/**
 * Cookie + header names. Not `__Host-` prefixed (D-053): the refresh cookie must be path-scoped to
 * `/api/v1/auth`, which that prefix forbids, and the CSRF cookie must be readable by the page. In
 * production all of them are `Secure; SameSite=Lax` (docs/SECURITY.md §A02).
 */
export const COOKIE_NAMES = {
  access: 'sbg_at',
  refresh: 'sbg_rt',
  csrf: 'sbg_csrf',
  oauth: 'sbg_oauth',
} as const;

export const CSRF_HEADER = 'x-csrf-token';
export const REQUEST_ID_HEADER = 'x-request-id';
export const IDEMPOTENCY_HEADER = 'idempotency-key';

export const API_PREFIX = '/api/v1';

/** Machine-readable error codes returned in `{ error: { code } }`. */
export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INVALID_TRANSITION: 'INVALID_TRANSITION',
  RATE_LIMITED: 'RATE_LIMITED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
  ACCOUNT_PENDING_DELETION: 'ACCOUNT_PENDING_DELETION',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  CSRF_FAILED: 'CSRF_FAILED',
  CAPTCHA_FAILED: 'CAPTCHA_FAILED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNSUPPORTED_MEDIA: 'UNSUPPORTED_MEDIA',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  LAST_SUPER_ADMIN: 'LAST_SUPER_ADMIN',
  INTERNAL: 'INTERNAL',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
